/**
 * trace.ts —— 调试追踪（控制台输出）
 *
 * 用户需求：在浏览器控制台看到**每一次、每一轮** AI 回答与解释器返回的完整内容。
 *
 * ── 为什么单独立一个模块，而不是各处散着写 console.log ──
 *
 * ① **轮次是一等公民**
 *    一轮交互最多有四段：翻译轮 → 执行 → 回答轮/失败二轮 → 收尾。
 *    散着写的话，控制台里四段输出样子一样，读的人得靠猜区分。
 *    这里用 `console.group` 把每一轮圈起来，并在标题里写明轮次与用途。
 *
 * ② **序号让「这是第几次交互」一目了然**
 *    每次 handleUserInput 递增一个序号，全部输出带 `#3` 这样的前缀。
 *    否则连问几句后，控制台里几轮输出混在一起根本分不清边界。
 *
 * ③ **开关只有一处**
 *    调试模式（AppSettings.debugMode）的判定收在这里。
 *    AI 层、解释器层只管调 trace.xxx()，不关心开关 —— 保持层间解耦。
 *
 * ④ **用完即弃**
 *    为什么不用 console.debug：debug 级别在 DevTools 里默认被
 *    Verbose 过滤器折叠，用户「要在控制台看到」的需求反而看不到。
 *    改用 log，且只在开关打开时输出，正常用户的控制台依然是干净的。
 *
 * ── 控制台里长什么样 ──
 *
 *   ▶ 灵讯 #3 「把主题切成黑夜模式」            ← 一整轮折叠在此
 *      第 1 轮 · 翻译  [模型原文]  {...}
 *      第 1 轮 · 清洗后 IL  theme.set('dark')
 *      执行 · 逐条结果 (2) [{…}, {…}]
 *      收尾 · 最终文案  已切换为黑夜模式
 */

import { settingsRepo } from '@/settings/settings';
import type { EvalResult } from '@/types/il';

/** 全局交互序号。每进一次 handleUserInput 加一，用于在控制台区分轮次 */
let seq = 0;

/** 当前轮次的分组标签，供 endTurn 时收尾 */
let groupOpen = false;

function enabled(): boolean {
  return settingsRepo.getApp().debugMode;
}

/**
 * 结构化打印一个值。
 *
 * 有个细节值得注意：DevTools 里 console.log(obj) 打印的是**引用**，
 * 展开时看到的是对象「现在」的样子，不一定是你打印那一刻的样子。
 * 对于 EvalResult 这种打印完还会被后续逻辑改动的对象，
 * 这会造成「看到的不是当时的」的困惑。所以统一走 JSON 快照 ——
 * 虽然牺牲了点击展开的便利，但胜在忠实。
 */
function snap(value: unknown): unknown {
  if (value === undefined || value === null) return value;
  if (typeof value !== 'object') return value;
  try {
    // structuredClone 会因 EvalResult 里可能的函数字段而失败，
    // 所以走 JSON 序列化再解析 —— 这两类对象都是纯数据。
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

export const trace = {
  /** 开一轮新的交互分组。返回的序号可用于后续日志标题 */
  beginTurn(userInput: string): number {
    if (!enabled()) return 0;
    seq += 1;
    groupOpen = true;
    console.group(
      `%c灵讯 #${seq}%c ${userInput}`,
      'color:#5C7A00;font-weight:bold',
      'color:inherit',
    );
    return seq;
  },

  /** 关闭当前分组。没开分组时调用无副作用 */
  endTurn(): void {
    if (!enabled() || !groupOpen) return;
    groupOpen = false;
    console.groupEnd();
  },

  /**
   * 模型某一轮的原始返回。
   *
   * @param round 轮次说明，如「第 1 轮 · 翻译」「第 2 轮 · 回答」
   * @param raw   模型原文（未清洗）
   * @param code  清洗后的 IL 或文本
   */
  modelRaw(round: string, raw: string, code: string): void {
    if (!enabled()) return;
    console.log(`  ${round} · 模型原文：\n${raw || '(空)'}`);
    console.log(`  ${round} · 清洗后：${code || '(空)'}`);
  },

  /**
   * 请求提示词。
   *
   * 排查「AI 为什么翻译歪了」时，提示词是决定性的证据 —— 但它在正常
   * 使用中信息量极低（几千字的固定模板，每轮都一样）。所以整块折叠，
   * 且只在此刻打印；平时误展开也只是多看一眼模板，不影响主流程。
   *
   * 标签上带字数：一眼能看出这轮 prompt 是否异常膨胀。
   */
  prompt(round: string, systemPrompt: string, userMessage: string): void {
    if (!enabled()) return;
    const size = systemPrompt.length + userMessage.length;
    console.groupCollapsed(`  ${round} · 提示词（${size} 字，点开查看）`);
    console.log('system：', systemPrompt);
    console.log('user：', userMessage);
    console.groupEnd();
  },

  /** 解析失败（语法错误，说明模型没写好 IL） */
  parseError(code: string, kind: string, message: string): void {
    if (!enabled()) return;
    console.warn(`  解析失败 · ${kind}：${message}`, {
      '模型给出的 IL': code,
    });
  },

  /**
   * 解释器逐条执行结果 —— 这是用户最想看的「解释器返回的内容」。
   *
   * 逐条平铺而不是打个数组：数组折叠起来看不见内容，
   * 而每条命令的成败恰恰是要一眼扫过的东西。图片/文件句柄等
   * 大对象在 value 里，所以用 snap 做快照避免引用漂移。
   */
  evalResults(results: EvalResult[]): void {
    if (!enabled()) return;
    console.groupCollapsed(`  执行 · 逐条结果（${results.length} 条）`);
    results.forEach((r, i) => {
      const mark = r.ok ? '✓' : '✗';
      const head = `${mark} [${i + 1}] ${r.command}`;
      const detail: Record<string, unknown> = {};
      if (r.desc) detail['文案'] = r.desc;
      if (r.isQuery) detail['类型'] = '查询 [查]';
      if (r.descIsReceipt) detail['文案来源'] = '查类机械回执';
      if (r.value !== undefined) detail['数据'] = snap(r.value);
      if (r.error) detail['错误'] = `${r.error.kind}：${r.error.message}`;

      if (r.ok) console.log(head, detail);
      else console.warn(head, detail);
    });
    console.groupEnd();
  },

  /** 回喂给模型的真实数据/错误（回答轮与失败二轮共用） */
  feedback(round: string, payload: string): void {
    if (!enabled()) return;
    console.log(`  ${round} · 回喂内容：\n${payload}`);
  },

  /** 本轮最终决定（走了哪条路径、给用户的最终文案） */
  outcome(path: '成功' | '失败二轮' | '回答轮' | '无指令', text: string): void {
    if (!enabled()) return;
    console.log(`  收尾 · 路径=${path}`);
    console.log(`  收尾 · 最终文案：\n${text}`);
  },

  /** 通用备注 */
  note(label: string, payload?: unknown): void {
    if (!enabled()) return;
    if (payload === undefined) console.log(`  ${label}`);
    else console.log(`  ${label}`, snap(payload));
  },
};
